import { HttpClient, HttpHeaders } from "@angular/common/http";
import { Injectable } from "@angular/core";
import { PlatformService } from "./platform/platform.service";
import { GlobalConstants } from "../global-constants";

interface TimingData {
  page_load_time: number;
  page_download_time: number;
  dns_time: number;
  redirect_response_time: number;
  server_response_time: number;
  tcp_connect_time: number;
  dom_interactive_time: number;
  content_load_time: number;
}

const API_URL = "https://tribuna.isquad.es/json/api/call.php";

@Injectable({
  providedIn: "root",
})
export class PerformanceTrackingService {
  private readonly siteSpeedSampleRate: number = 100;
  private readonly gaCookieName: string = "_ga";
  private readonly dataLayerName: string = "dataLayer";

  private API_URL: string;

  constructor(
    private httpClient: HttpClient,
    private platformService: PlatformService,
  ) {
    const host = this.platformService.getLocationHostname();
    const globalConstants: any = new GlobalConstants(host);
    this.API_URL = globalConstants.urlApi;
  }

  public sendPerformanceMetrics(metrics: TimingData | null): void {
    if (!metrics) return;

    fetch(`${API_URL}/tribuna/webvitals`, {
      method: "POST",
      body: JSON.stringify({ ...metrics, url: window.location.href }),
    })
      .then((r) => console.log(r.status ? "webvitals!" : "webvitals :("))
      .catch((r) => console.error("webvitals :(("));
  }

  /**
   * Obtiene y procesa las métricas de rendimiento de la página
   * @returns TimingData | null - Datos de rendimiento o null si no se pueden obtener
   */
  public getPerformanceMetrics(): TimingData | null {
    if (!this.shouldTrack()) {
      return null;
    }

    const performanceTiming = this.getPerformanceTiming();
    if (!performanceTiming) {
      return null;
    }

    const timingData = this.calculateTimingData(performanceTiming);
    if (!this.isValidTimingData(timingData)) {
      return null;
    }

    // Opcional: enviar a dataLayer si existe
    this.pushToDataLayer(timingData);

    return timingData;
  }

  private shouldTrack(): boolean {
    const sampleRate = Math.min(this.siteSpeedSampleRate || 1, 100);
    const clientId = this.getClientId();

    if (!clientId) {
      return false;
    }

    return this.hashId(clientId) % 100 < sampleRate;
  }

  private hashId(str: string): number {
    let hash = 1;
    if (str) {
      hash = 0;
      for (let i = str.length - 1; i >= 0; i--) {
        const charCode = str.charCodeAt(i);
        hash = ((hash << 6) & 268435455) + charCode + (charCode << 14);
        const d = hash & 266338304;
        hash = d !== 0 ? hash ^ (d >> 21) : hash;
      }
    }
    return hash;
  }

  private getClientId(): string | null {
    const cookie = `; ${document.cookie}`;
    const cookieValue = cookie.split(`; ${this.gaCookieName}=`).pop()?.split(";")[0];
    if (!cookieValue) {
      return null;
    }
    return cookieValue.split(/GA1\.[0-9]\./)[1] || null;
  }

  private getPerformanceTiming(): PerformanceTiming | null {
    const pt = window.performance || (window as any).webkitPerformance;
    const timing = pt?.timing;

    if (!timing || timing.navigationStart === 0 || timing.loadEventStart === 0) {
      return null;
    }

    return timing;
  }

  private calculateTimingData(timing: PerformanceTiming): TimingData {
    return {
      page_load_time: timing.loadEventStart - timing.navigationStart,
      page_download_time: timing.responseEnd - timing.responseStart,
      dns_time: timing.domainLookupEnd - timing.domainLookupStart,
      redirect_response_time: timing.fetchStart - timing.navigationStart,
      server_response_time: timing.responseStart - timing.requestStart,
      tcp_connect_time: timing.connectEnd - timing.connectStart,
      dom_interactive_time: timing.domInteractive - timing.navigationStart,
      content_load_time: timing.domContentLoadedEventStart - timing.navigationStart,
    };
  }

  private isValidTimingData(timingData: TimingData): boolean {
    return Object.values(timingData).every((value) => value >= 0);
  }

  private pushToDataLayer(timingData: TimingData): void {
    if (window[this.dataLayerName]) {
      (window[this.dataLayerName] as any[]).push({
        event: "performance_timing",
        timing: timingData,
      });
    }
  }
}
